((global, factory) => {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (!global?.document) return;

  if (global.TimeseekMarketPriceChart) {
    global.TimeseekMarketPriceChart.boot(global.document);
    return;
  }

  global.TimeseekMarketPriceChart = api;

  const start = () => api.boot(global.document);
  if (global.document.readyState === 'loading') {
    global.document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  global.document.addEventListener('shopify:section:load', (event) => api.boot(event.target));
})(typeof window !== 'undefined' ? window : globalThis, () => {
  'use strict';

  const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
  const DAY_IN_MS = 24 * 60 * 60 * 1000;
  const RANGE_OPTIONS = Object.freeze({
    '30d': { days: 30, dayTickStep: 7 },
    '3m': { months: 3, dayTickStep: 15 },
    '6m': { months: 6, dayTickStep: 30 },
    '1y': { months: 12, dayTickStep: 60 },
  });
  const INTEGER_FORMATTER = new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 });
  const DEFAULT_PRICE_OUTLIER_THRESHOLD = 100_000_000;

  const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

  // 999,999,999 のような 9 だけの値（整数部8桁以上）は掲載価格ではなくプレースホルダとして扱う。
  const isPlaceholderPrice = (value) => {
    if (!isFiniteNumber(value)) return false;
    const integerDigits = String(Math.trunc(Math.abs(value)));
    return integerDigits.length >= 8 && /^9+$/.test(integerDigits);
  };

  const resolveOutlierThreshold = (threshold) =>
    isFiniteNumber(threshold) && threshold > 0 ? threshold : DEFAULT_PRICE_OUTLIER_THRESHOLD;

  const isPlausiblePrice = (value, threshold = DEFAULT_PRICE_OUTLIER_THRESHOLD) =>
    isFiniteNumber(value) &&
    value > 0 &&
    value < resolveOutlierThreshold(threshold) &&
    !isPlaceholderPrice(value);

  // 掲載価格レンジの片側でも外れ値なら、その日のレンジは描かない（別の値で補わない）。
  const sanitizePriceRange = (points, threshold = DEFAULT_PRICE_OUTLIER_THRESHOLD) =>
    points.map((point) => {
      if (point.lower === null && point.upper === null) return point;
      if (isPlausiblePrice(point.lower, threshold) && isPlausiblePrice(point.upper, threshold)) return point;
      return { ...point, lower: null, upper: null };
    });

  const toFiniteNumber = (value) => {
    if (isFiniteNumber(value)) return value;
    if (typeof value !== 'string') return null;

    const normalized = value.replace(/[\s,円本]/g, '');
    if (normalized === '') return null;
    const number = Number(normalized);
    return Number.isFinite(number) ? number : null;
  };

  const createUtcDate = (year, month, day) => {
    const date = new Date(Date.UTC(year, month - 1, day));
    if (
      date.getUTCFullYear() !== year ||
      date.getUTCMonth() !== month - 1 ||
      date.getUTCDate() !== day
    ) {
      return null;
    }
    return date;
  };

  const parseDateValue = (value) => {
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) return null;
      return createUtcDate(value.getUTCFullYear(), value.getUTCMonth() + 1, value.getUTCDate());
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      const timestamp = value < 1_000_000_000_000 ? value * 1000 : value;
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) return null;
      return createUtcDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
    }

    if (typeof value !== 'string') return null;
    const raw = value.trim();
    if (raw === '') return null;

    const dayMatch = raw.match(/^(\d{4})[-/.年](\d{1,2})(?:[-/.月](\d{1,2}))?/);
    if (dayMatch) {
      return createUtcDate(Number(dayMatch[1]), Number(dayMatch[2]), Number(dayMatch[3] || 1));
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return createUtcDate(parsed.getUTCFullYear(), parsed.getUTCMonth() + 1, parsed.getUTCDate());
  };

  const dateToKey = (value) => {
    const date = parseDateValue(value);
    if (!date) return '';
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const firstDefined = (object, keys) => {
    for (const key of keys) {
      if (object?.[key] !== undefined && object[key] !== null && object[key] !== '') {
        return object[key];
      }
    }
    return null;
  };

  const normalizePoint = (point) => {
    if (point && isFiniteNumber(point.timestamp) && isFiniteNumber(point.median)) {
      return point;
    }

    if (!point || (typeof point !== 'object' && typeof point !== 'number')) return null;
    const object = typeof point === 'object' ? point : { value: point };
    const date = parseDateValue(
      firstDefined(object, ['date', 'day', 'updated_at', 'timestamp', 'period_label', 'month', 'label']),
    );
    const median = toFiniteNumber(
      firstDefined(object, ['value', 'median_value', 'median', 'price', 'amount', 'market_price', 'close']),
    );
    if (!date || median === null) return null;

    const rawLower = toFiniteNumber(firstDefined(object, ['min', 'price_min', 'range_min', 'low', 'lower']));
    const rawUpper = toFiniteNumber(firstDefined(object, ['max', 'price_max', 'range_max', 'high', 'upper']));
    const inventory = toFiniteNumber(
      firstDefined(object, ['inventory_count', 'available_count', 'inventory', 'listing_count', 'market_count']),
    );
    const hasRange = rawLower !== null && rawUpper !== null;

    return {
      date,
      dateKey: dateToKey(date),
      timestamp: date.getTime(),
      median,
      lower: hasRange ? Math.min(rawLower, rawUpper) : null,
      upper: hasRange ? Math.max(rawLower, rawUpper) : null,
      inventory,
      source: object.source || '',
    };
  };

  const normalizePoints = (data) => {
    const candidates = Array.isArray(data)
      ? data
      : data?.points || data?.series || data?.data || [];
    if (!Array.isArray(candidates)) return [];

    const pointsByDate = new Map();
    candidates.forEach((candidate) => {
      const point = normalizePoint(candidate);
      if (point) pointsByDate.set(point.dateKey, point);
    });

    return [...pointsByDate.values()].sort((left, right) => left.timestamp - right.timestamp);
  };

  const getLatestDate = (data) => {
    const points = normalizePoints(data);
    if (points.length === 0) return null;
    return new Date(points[points.length - 1].timestamp);
  };

  const addUtcDays = (date, amount) => new Date(date.getTime() + amount * DAY_IN_MS);

  const subtractUtcMonths = (date, amount) => {
    const targetMonthStart = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - amount, 1));
    const targetMonthEnd = new Date(
      Date.UTC(targetMonthStart.getUTCFullYear(), targetMonthStart.getUTCMonth() + 1, 0),
    );
    return createUtcDate(
      targetMonthStart.getUTCFullYear(),
      targetMonthStart.getUTCMonth() + 1,
      Math.min(date.getUTCDate(), targetMonthEnd.getUTCDate()),
    );
  };

  const getRangeStart = (latestDate, range) => {
    const latest = parseDateValue(latestDate);
    const option = RANGE_OPTIONS[range] || RANGE_OPTIONS['30d'];
    if (!latest) return null;
    if (option.days) return addUtcDays(latest, -(option.days - 1));
    return addUtcDays(subtractUtcMonths(latest, option.months), 1);
  };

  const filterByRange = (data, range, latestDate = null) => {
    const points = normalizePoints(data);
    if (points.length === 0) return [];

    const latest = parseDateValue(latestDate) || new Date(points[points.length - 1].timestamp);
    const start = getRangeStart(latest, range);
    if (!start) return [];

    return points.filter((point) => point.timestamp >= start.getTime() && point.timestamp <= latest.getTime());
  };

  const average = (values) => {
    const numbers = values.filter(isFiniteNumber);
    if (numbers.length === 0) return null;
    return numbers.reduce((sum, value) => sum + value, 0) / numbers.length;
  };

  const aggregateByMonth = (data) => {
    const points = normalizePoints(data);
    const groups = new Map();

    points.forEach((point) => {
      const monthKey = point.dateKey.slice(0, 7);
      if (!groups.has(monthKey)) groups.set(monthKey, []);
      groups.get(monthKey).push(point);
    });

    return [...groups.entries()].map(([monthKey, monthPoints]) => {
      const date = parseDateValue(`${monthKey}-01`);
      const inventoryAverage = average(monthPoints.map((point) => point.inventory));
      const timestamp = average(monthPoints.map((point) => point.timestamp));
      return {
        date,
        dateKey: monthKey,
        timestamp,
        median: average(monthPoints.map((point) => point.median)),
        lower: average(monthPoints.map((point) => point.lower)),
        upper: average(monthPoints.map((point) => point.upper)),
        inventory: inventoryAverage === null ? null : Math.round(inventoryAverage),
        sampleCount: monthPoints.length,
        source: 'monthly_average',
      };
    });
  };

  const formatPriceAxis = (value) => (isFiniteNumber(value) ? INTEGER_FORMATTER.format(Math.round(value)) : '—');
  const formatCompactPriceAxis = (value) => {
    if (!isFiniteNumber(value)) return '—';
    if (Math.abs(value) >= 100_000_000) return `${Math.round((value / 100_000_000) * 10) / 10}億`;
    if (Math.abs(value) >= 10_000) return `${Math.round(value / 10_000)}万`;
    return formatPriceAxis(value);
  };
  const formatInventoryAxis = (value) =>
    isFiniteNumber(value) ? INTEGER_FORMATTER.format(Math.round(value)) : '—';

  const formatXAxisLabel = (dateValue, granularity = 'day') => {
    const date = parseDateValue(dateValue);
    if (!date) return '';
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    if (granularity === 'month') return `${year}/${month}`;
    return `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
  };

  const formatDateForTooltip = (dateValue, granularity = 'day') => {
    const date = parseDateValue(dateValue);
    if (!date) return '';
    if (granularity === 'month') {
      return `${date.getUTCFullYear()}年${date.getUTCMonth() + 1}月`;
    }
    return `${date.getUTCFullYear()}年${date.getUTCMonth() + 1}月${date.getUTCDate()}日`;
  };

  const formatCurrency = (value) =>
    isFiniteNumber(value) ? `${INTEGER_FORMATTER.format(Math.round(value))}円` : '—';
  const formatListingCount = (value) =>
    isFiniteNumber(value) ? `${INTEGER_FORMATTER.format(Math.round(value))}件` : '—';

  const buildTooltipRows = (point, options = {}) => {
    const hasRange = isFiniteNumber(point.lower) && isFiniteNumber(point.upper);
    const rows = [
      ['掲載価格レンジ', hasRange ? `${formatCurrency(point.lower)}〜${formatCurrency(point.upper)}` : '—'],
      ['中央値', formatCurrency(point.median)],
    ];
    if (options.showListingCount && options.listingCountLabel) {
      rows.push([options.listingCountLabel, formatListingCount(point.inventory)]);
    }
    return rows;
  };

  // The point count is given in days/months (「30日分」「12か月分」), not 件, so it is not
  // heard as a listing count (proofread 2026-09-30).
  const buildChartSummaryText = ({
    rangeLabel,
    granularityLabel,
    pointCount,
    pointUnit = '日分',
    latestPoint,
    showListingCount = false,
    listingCountLabel = '',
  }) => {
    const base = `${rangeLabel}・${granularityLabel}を表示中（${pointCount}${pointUnit}）。最新の中央値は${formatCurrency(latestPoint.median)}`;
    if (showListingCount && listingCountLabel) {
      return `${base}、${listingCountLabel}は${formatListingCount(latestPoint.inventory)}です。`;
    }
    return `${base}です。`;
  };

  const niceStep = (roughStep) => {
    if (!isFiniteNumber(roughStep) || roughStep <= 0) return 1;
    const exponent = Math.floor(Math.log10(roughStep));
    const fraction = roughStep / 10 ** exponent;
    const niceFraction = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
    return niceFraction * 10 ** exponent;
  };

  const niceLogBoundary = (value, direction) => {
    const exponent = Math.floor(Math.log10(value));
    const magnitude = 10 ** exponent;
    const fraction = value / magnitude;
    const steps = [1, 2, 5, 10];
    if (direction === 'floor') {
      const step = [...steps].reverse().find((candidate) => candidate <= fraction) || 1;
      return step * magnitude;
    }
    const step = steps.find((candidate) => candidate >= fraction) || 10;
    return step * magnitude;
  };

  const createLogTicks = (minimum, maximum, tickCount) => {
    const candidates = [];
    const startExponent = Math.floor(Math.log10(minimum));
    const endExponent = Math.ceil(Math.log10(maximum));
    for (let exponent = startExponent; exponent <= endExponent; exponent += 1) {
      [1, 2, 5].forEach((fraction) => {
        const value = fraction * 10 ** exponent;
        if (value >= minimum && value <= maximum) candidates.push(value);
      });
    }
    if (!candidates.includes(minimum)) candidates.unshift(minimum);
    if (!candidates.includes(maximum)) candidates.push(maximum);
    if (candidates.length <= tickCount) return candidates;

    const ticks = [];
    for (let index = 0; index < tickCount; index += 1) {
      const candidateIndex = Math.round((index * (candidates.length - 1)) / (tickCount - 1));
      const candidate = candidates[candidateIndex];
      if (!ticks.includes(candidate)) ticks.push(candidate);
    }
    return ticks;
  };

  const createPriceScale = (points, tickCount = 5) => {
    const values = points.flatMap((point) => [point.lower, point.median, point.upper]).filter(isFiniteNumber);
    if (values.length === 0) return null;

    let minimum = Math.min(...values);
    let maximum = Math.max(...values);
    if (minimum === maximum) {
      const padding = Math.max(Math.abs(maximum) * 0.05, 1000);
      minimum = Math.max(0, minimum - padding);
      maximum += padding;
    }

    if (minimum > 0 && maximum / minimum >= 20) {
      const niceMinimum = niceLogBoundary(minimum, 'floor');
      const niceMaximum = niceLogBoundary(maximum, 'ceil');
      return {
        minimum: niceMinimum,
        maximum: niceMaximum,
        ticks: createLogTicks(niceMinimum, niceMaximum, tickCount),
        type: 'log',
        transform: (value) => Math.log10(Math.max(value, niceMinimum)),
      };
    }

    const step = niceStep((maximum - minimum) / Math.max(tickCount - 1, 1));
    const niceMinimum = Math.max(0, Math.floor(minimum / step) * step);
    let niceMaximum = Math.ceil(maximum / step) * step;
    if (niceMaximum <= niceMinimum) niceMaximum = niceMinimum + step;
    const ticks = [];
    for (let index = 0; index < tickCount; index += 1) {
      ticks.push(niceMinimum + ((niceMaximum - niceMinimum) * index) / (tickCount - 1));
    }

    return { minimum: niceMinimum, maximum: niceMaximum, ticks, type: 'linear', transform: (value) => value };
  };

  const createInventoryScale = (points, tickCount = 5) => {
    const values = points.map((point) => point.inventory).filter(isFiniteNumber);
    if (values.length === 0) return { minimum: 0, maximum: 4, ticks: [0, 1, 2, 3, 4] };

    const maximumValue = Math.max(...values, 1);
    const step = Math.max(1, niceStep(maximumValue / Math.max(tickCount - 1, 1)));
    const maximum = step * (tickCount - 1);
    const ticks = [];
    for (let index = 0; index < tickCount; index += 1) {
      ticks.push(step * index);
    }
    return { minimum: 0, maximum, ticks };
  };

  const createSvgNode = (name, attributes = {}, text = '') => {
    const node = document.createElementNS(SVG_NAMESPACE, name);
    Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, String(value)));
    if (text !== '') node.textContent = text;
    return node;
  };

  const appendSvgNode = (parent, name, attributes = {}, text = '') => {
    const node = createSvgNode(name, attributes, text);
    parent.appendChild(node);
    return node;
  };

  const linePath = (points, valueKey, xScale, yScale) => {
    const validPoints = points.filter((point) => isFiniteNumber(point[valueKey]));
    return validPoints
      .map((point, index) => `${index === 0 ? 'M' : 'L'}${xScale(point.timestamp)},${yScale(point[valueKey])}`)
      .join(' ');
  };

  const rangeAreaPath = (points, xScale, yScale) => {
    // 除外した日をまたいで帯をつなげないよう、連続した有効点ごとに閉じた帯を作る。
    const runs = [];
    let current = [];
    points.forEach((point) => {
      if (isFiniteNumber(point.lower) && isFiniteNumber(point.upper)) {
        current.push(point);
      } else if (current.length > 0) {
        runs.push(current);
        current = [];
      }
    });
    if (current.length > 0) runs.push(current);

    return runs
      .filter((run) => run.length >= 2)
      .map((run) => {
        const upperPath = run
          .map((point, index) => `${index === 0 ? 'M' : 'L'}${xScale(point.timestamp)},${yScale(point.upper)}`)
          .join(' ');
        const lowerPath = [...run]
          .reverse()
          .map((point) => `L${xScale(point.timestamp)},${yScale(point.lower)}`)
          .join(' ');
        return `${upperPath} ${lowerPath} Z`;
      })
      .join(' ');
  };

  const generateDayTicks = (startDate, endDate, range, compact = false) => {
    const option = RANGE_OPTIONS[range] || RANGE_OPTIONS['30d'];
    const tickStep = option.dayTickStep * (compact ? 2 : 1);
    const ticks = [];
    let cursor = new Date(startDate.getTime());
    while (cursor.getTime() <= endDate.getTime()) {
      ticks.push(cursor);
      cursor = addUtcDays(cursor, tickStep);
    }
    return ticks;
  };

  const generateMonthTicks = (startDate, endDate, maximumTicks = 7) => {
    const ticks = [new Date(startDate.getTime())];
    let cursor = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth() + 1, 1));
    while (cursor.getTime() <= endDate.getTime()) {
      ticks.push(cursor);
      cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
    }
    if (ticks.length <= maximumTicks) return ticks;

    const interval = Math.ceil(ticks.length / maximumTicks);
    const sampled = ticks.filter((_, index) => index % interval === 0);
    const last = ticks[ticks.length - 1];
    if (sampled[sampled.length - 1]?.getTime() !== last.getTime()) sampled.push(last);
    return sampled;
  };

  // 件数の右軸を出さないときも、右端の月ラベル（例: 2026/10）が切れない余白を残す。
  const getChartLayout = (root, showListingCount = true) => {
    const measuredWidth = root.getBoundingClientRect?.().width || root.clientWidth || 760;
    const compact = measuredWidth < 520;
    return compact
      ? {
          compact: true,
          width: 480,
          height: 360,
          fontSize: 16,
          titleFontSize: 16,
          plot: { top: 42, right: showListingCount ? 66 : 36, bottom: 58, left: 82 },
        }
      : {
          compact: false,
          width: 760,
          height: 360,
          fontSize: 16,
          titleFontSize: 16,
          plot: { top: 38, right: showListingCount ? 82 : 36, bottom: 52, left: 100 },
        };
  };

  const setChartState = (root, state, message = '') => {
    root.querySelectorAll('[data-ts-market-chart-state]').forEach((element) => {
      const matches = element.dataset.tsMarketChartState === state;
      element.hidden = !matches;
      if (matches && message) element.textContent = message;
    });

    const canvas = root.querySelector('[data-ts-market-chart-canvas]');
    if (canvas) canvas.hidden = state !== 'ready';
  };

  const updateControls = (root, selectedRange, selectedGranularity) => {
    root.querySelectorAll('[data-ts-market-chart-range]').forEach((button) => {
      const selected = button.dataset.tsMarketChartRange === selectedRange;
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.classList.toggle('is-active', selected);
    });
    root.querySelectorAll('[data-ts-market-chart-granularity]').forEach((button) => {
      const selected = button.dataset.tsMarketChartGranularity === selectedGranularity;
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.classList.toggle('is-active', selected);
    });
  };

  const renderTooltipContent = (tooltip, point, granularity, options = {}) => {
    tooltip.replaceChildren();
    const suffix = granularity === 'month' ? '（月平均）' : '';
    const heading = document.createElement('strong');
    heading.className = 'ts-brand-market__tooltip-heading';
    heading.textContent = formatDateForTooltip(point.date, granularity);
    tooltip.appendChild(heading);

    const rows = buildTooltipRows(point, options);
    rows.forEach(([label, value]) => {
      const row = document.createElement('span');
      row.className = 'ts-brand-market__tooltip-row';
      const labelNode = document.createElement('span');
      labelNode.textContent = `${label}${suffix}`;
      const valueNode = document.createElement('span');
      valueNode.textContent = value;
      row.append(labelNode, valueNode);
      tooltip.appendChild(row);
    });
  };

  const positionTooltip = (root, clientX, clientY) => {
    const tooltip = root.querySelector('[data-ts-market-chart-tooltip]');
    const stage = root.querySelector('.ts-brand-market__chart-stage');
    if (!tooltip || !stage) return;

    const stageRect = stage.getBoundingClientRect();
    tooltip.hidden = false;
    const gap = 12;
    let left = clientX - stageRect.left + gap;
    let top = clientY - stageRect.top - tooltip.offsetHeight - gap;
    if (left + tooltip.offsetWidth > stageRect.width - gap) {
      left = clientX - stageRect.left - tooltip.offsetWidth - gap;
    }
    left = Math.max(gap, Math.min(left, stageRect.width - tooltip.offsetWidth - gap));
    top = Math.max(gap, Math.min(top, stageRect.height - tooltip.offsetHeight - gap));
    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  };

  const getSvgClientPoint = (svg, x, y) => {
    const point = svg.createSVGPoint();
    point.x = x;
    point.y = y;
    const matrix = svg.getScreenCTM();
    return matrix ? point.matrixTransform(matrix) : { x: 0, y: 0 };
  };

  const showPoint = (root, index, clientPosition = null) => {
    const state = root.__tsMarketChartState;
    if (!state?.renderedPoints?.length) return;
    const safeIndex = Math.max(0, Math.min(index, state.renderedPoints.length - 1));
    const renderedPoint = state.renderedPoints[safeIndex];
    const tooltip = root.querySelector('[data-ts-market-chart-tooltip]');
    const activeGroup = root.querySelector('[data-ts-market-chart-active]');
    if (!tooltip || !activeGroup) return;

    state.activeIndex = safeIndex;
    renderTooltipContent(tooltip, renderedPoint.point, state.selectedGranularity, {
      showListingCount: state.showListingCount,
      listingCountLabel: state.listingCountLabel,
    });
    activeGroup.removeAttribute('hidden');
    const line = activeGroup.querySelector('line');
    const circle = activeGroup.querySelector('circle');
    line?.setAttribute('x1', renderedPoint.x);
    line?.setAttribute('x2', renderedPoint.x);
    circle?.setAttribute('cx', renderedPoint.x);
    circle?.setAttribute('cy', renderedPoint.medianY);

    const svg = root.querySelector('[data-ts-market-chart-svg]');
    const clientPoint = clientPosition || getSvgClientPoint(svg, renderedPoint.x, renderedPoint.medianY);
    positionTooltip(root, clientPoint.x, clientPoint.y);
  };

  const hidePoint = (root) => {
    const tooltip = root.querySelector('[data-ts-market-chart-tooltip]');
    const activeGroup = root.querySelector('[data-ts-market-chart-active]');
    if (tooltip) tooltip.hidden = true;
    if (activeGroup) activeGroup.setAttribute('hidden', '');
  };

  const renderChart = (root) => {
    const state = root.__tsMarketChartState;
    const filtered = filterByRange(state.allPoints, state.selectedRange, state.latestDate);
    const displayPoints =
      state.selectedGranularity === 'month' ? aggregateByMonth(filtered) : filtered;

    updateControls(root, state.selectedRange, state.selectedGranularity);
    if (displayPoints.length === 0) {
      setChartState(root, 'empty', '選択した期間に表示できるデータがありません。');
      return;
    }

    const priceScale = createPriceScale(displayPoints);
    if (!priceScale) {
      setChartState(root, 'empty', '選択した期間に表示できる価格データがありません。');
      return;
    }
    const showListingCount = Boolean(state.showListingCount && state.listingCountLabel);
    const inventoryScale = createInventoryScale(displayPoints);
    const rangeStart = getRangeStart(state.latestDate, state.selectedRange);
    const layout = getChartLayout(root, showListingCount);
    const { plot } = layout;
    const plotWidth = layout.width - plot.left - plot.right;
    const plotHeight = layout.height - plot.top - plot.bottom;
    state.compact = layout.compact;
    let xMinimum = rangeStart.getTime();
    let xMaximum = state.latestDate.getTime();
    if (xMinimum === xMaximum) {
      xMinimum -= DAY_IN_MS / 2;
      xMaximum += DAY_IN_MS / 2;
    }
    const xScale = (timestamp) =>
      plot.left + ((timestamp - xMinimum) / (xMaximum - xMinimum)) * plotWidth;
    const transformedPriceMinimum = priceScale.transform(priceScale.minimum);
    const transformedPriceMaximum = priceScale.transform(priceScale.maximum);
    const priceYScale = (value) => {
      const transformedValue = priceScale.transform(value);
      return (
        plot.top +
        ((transformedPriceMaximum - transformedValue) /
          (transformedPriceMaximum - transformedPriceMinimum)) *
          plotHeight
      );
    };
    const inventoryYScale = (value) =>
      plot.top + ((inventoryScale.maximum - value) / inventoryScale.maximum) * plotHeight;

    const svg = root.querySelector('[data-ts-market-chart-svg]');
    svg.replaceChildren();
    svg.setAttribute('viewBox', `0 0 ${layout.width} ${layout.height}`);
    const scaleDescription = priceScale.type === 'log' ? '価格軸は対数表示です。' : '';
    svg.setAttribute(
      'aria-label',
      `中央値推移。${state.selectedRangeLabel}、${state.selectedGranularity === 'month' ? '月別の月平均' : '日別'}。${scaleDescription}左右の矢印キーでデータを確認できます。`,
    );
    appendSvgNode(svg, 'title', {}, '中央値推移');
    appendSvgNode(
      svg,
      'desc',
      {},
      `${state.selectedRangeLabel}の掲載価格レンジ${showListingCount ? `、中央値、${state.listingCountLabel}` : 'と中央値'}を${state.selectedGranularity === 'month' ? '月平均' : '日別'}で表示しています。${scaleDescription}`,
    );
    appendSvgNode(svg, 'rect', {
      x: 0,
      y: 0,
      width: layout.width,
      height: layout.height,
      fill: '#fcfcfd',
    });

    priceScale.ticks.forEach((tick, index) => {
      const y = priceYScale(tick);
      appendSvgNode(svg, 'line', {
        x1: plot.left,
        y1: y,
        x2: layout.width - plot.right,
        y2: y,
        stroke: index === 0 ? '#cfd6df' : '#e3e8f0',
        'stroke-width': 1,
        'stroke-dasharray': index === 0 ? '0' : '4 4',
      });
      appendSvgNode(
        svg,
        'text',
        {
          x: plot.left - 10,
          y: y + 4,
          'text-anchor': 'end',
          'font-size': layout.fontSize,
          fill: '#546075',
        },
        layout.compact ? formatCompactPriceAxis(tick) : formatPriceAxis(tick),
      );
    });

    (showListingCount ? inventoryScale.ticks : []).forEach((tick) => {
      const y = inventoryYScale(tick);
      appendSvgNode(svg, 'line', {
        x1: layout.width - plot.right,
        y1: y,
        x2: layout.width - plot.right + 5,
        y2: y,
        stroke: '#8b96a8',
        'stroke-width': 1,
      });
      appendSvgNode(
        svg,
        'text',
        {
          x: layout.width - plot.right + 10,
          y: y + 4,
          'text-anchor': 'start',
          'font-size': layout.fontSize,
          fill: '#546075',
        },
        formatInventoryAxis(tick),
      );
    });

    appendSvgNode(svg, 'line', {
      x1: plot.left,
      y1: plot.top,
      x2: plot.left,
      y2: layout.height - plot.bottom,
      stroke: '#687387',
      'stroke-width': 1.1,
    });
    if (showListingCount) {
      appendSvgNode(svg, 'line', {
        x1: layout.width - plot.right,
        y1: plot.top,
        x2: layout.width - plot.right,
        y2: layout.height - plot.bottom,
        stroke: '#8b96a8',
        'stroke-width': 1,
      });
    }
    appendSvgNode(
      svg,
      'text',
      { x: plot.left, y: 20, 'font-size': layout.titleFontSize, fill: '#172133' },
      '価格（円）',
    );
    if (showListingCount) {
      appendSvgNode(
        svg,
        'text',
        {
          x: layout.width - plot.right,
          y: 20,
          'text-anchor': 'end',
          'font-size': layout.titleFontSize,
          fill: '#172133',
        },
        `${state.listingCountLabel}（件）`,
      );
    }

    const areaPath = rangeAreaPath(displayPoints, xScale, priceYScale);
    let rangeDrawn = Boolean(areaPath);
    if (areaPath) {
      appendSvgNode(svg, 'path', {
        d: areaPath,
        fill: '#f2b879',
        opacity: 0.2,
        stroke: 'none',
      });
    } else if (
      displayPoints.length === 1 &&
      isFiniteNumber(displayPoints[0].lower) &&
      isFiniteNumber(displayPoints[0].upper)
    ) {
      const point = displayPoints[0];
      const x = xScale(point.timestamp);
      appendSvgNode(svg, 'rect', {
        x: x - 8,
        y: priceYScale(point.upper),
        width: 16,
        height: Math.max(2, priceYScale(point.lower) - priceYScale(point.upper)),
        fill: '#f2b879',
        opacity: 0.24,
      });
      rangeDrawn = true;
    }
    const rangeLegend = root.querySelector('[data-ts-market-chart-legend="range"]');
    if (rangeLegend) rangeLegend.hidden = !rangeDrawn;

    const barWidth = Math.max(2, Math.min(12, (plotWidth / Math.max(displayPoints.length, 1)) * 0.48));
    displayPoints.forEach((point) => {
      if (!showListingCount || !isFiniteNumber(point.inventory)) return;
      const x = xScale(point.timestamp);
      const y = inventoryYScale(point.inventory);
      appendSvgNode(svg, 'rect', {
        x: x - barWidth / 2,
        y,
        width: barWidth,
        height: Math.max(1, layout.height - plot.bottom - y),
        fill: '#0e5b53',
        opacity: 0.48,
      });
    });

    const medianPath = linePath(displayPoints, 'median', xScale, priceYScale);
    if (medianPath) {
      appendSvgNode(svg, 'path', {
        d: medianPath,
        fill: 'none',
        stroke: '#cb6f0b',
        'stroke-width': 3.5,
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
      });
    }
    if (displayPoints.length === 1) {
      appendSvgNode(svg, 'circle', {
        cx: xScale(displayPoints[0].timestamp),
        cy: priceYScale(displayPoints[0].median),
        r: 4.5,
        fill: '#cb6f0b',
      });
    }

    const xTicks =
      state.selectedGranularity === 'month'
        ? generateMonthTicks(rangeStart, state.latestDate, layout.compact ? 4 : 7)
        : generateDayTicks(rangeStart, state.latestDate, state.selectedRange, layout.compact);
    xTicks.forEach((tick) => {
      const x = xScale(tick.getTime());
      appendSvgNode(svg, 'line', {
        x1: x,
        y1: layout.height - plot.bottom,
        x2: x,
        y2: layout.height - plot.bottom + 5,
        stroke: '#687387',
        'stroke-width': 1,
      });
      appendSvgNode(
        svg,
        'text',
        {
          x,
          y: layout.height - 20,
          'text-anchor': 'middle',
          'font-size': layout.fontSize,
          fill: '#546075',
        },
        formatXAxisLabel(tick, state.selectedGranularity),
      );
    });

    const activeGroup = appendSvgNode(svg, 'g', {
      'data-ts-market-chart-active': '',
      hidden: '',
      'aria-hidden': 'true',
    });
    appendSvgNode(activeGroup, 'line', {
      y1: plot.top,
      y2: layout.height - plot.bottom,
      stroke: '#182131',
      'stroke-width': 1,
      'stroke-dasharray': '3 3',
      opacity: 0.5,
    });
    appendSvgNode(activeGroup, 'circle', {
      r: 5.5,
      fill: '#ffffff',
      stroke: '#cb6f0b',
      'stroke-width': 3,
    });

    state.renderedPoints = displayPoints.map((point) => ({
      point,
      x: xScale(point.timestamp),
      medianY: priceYScale(point.median),
    }));
    state.activeIndex = Math.max(0, state.renderedPoints.length - 1);

    const rangeLabel = state.selectedRangeLabel;
    const granularityLabel = state.selectedGranularity === 'month' ? '月別（月平均）' : '日別';
    const latestPoint = displayPoints[displayPoints.length - 1];
    const summary = root.querySelector('[data-ts-market-chart-summary]');
    if (summary) {
      summary.textContent = buildChartSummaryText({
        rangeLabel,
        granularityLabel,
        pointCount: displayPoints.length,
        pointUnit: state.selectedGranularity === 'month' ? 'か月分' : '日分',
        latestPoint,
        showListingCount,
        listingCountLabel: state.listingCountLabel,
      });
    }

    const detail = root.querySelector('[data-ts-market-chart-detail]');
    if (detail) {
      detail.hidden = displayPoints.length > 1;
      detail.textContent = displayPoints.length === 1 ? '表示できるデータが1件のため、点で表示しています。' : '';
    }
    const scaleNote = root.querySelector('[data-ts-market-chart-scale-note]');
    if (scaleNote) scaleNote.hidden = priceScale.type !== 'log';
    setChartState(root, 'ready');
  };

  const readPoints = (root) => {
    const script = root.querySelector('[data-ts-market-chart-data]');
    if (!script) throw new Error('Chart data element is missing.');
    const threshold = toFiniteNumber(root.dataset?.priceOutlierThreshold ?? '');
    return sanitizePriceRange(normalizePoints(JSON.parse(script.textContent || '[]')), threshold);
  };

  const resolveUpdatedDate = (root, latestDate) => {
    const configured = parseDateValue(root.dataset.updatedAt);
    return configured || latestDate;
  };

  const initialize = (root) => {
    if (!root || root.dataset.tsMarketPriceChartReady === 'true') return;
    root.dataset.tsMarketPriceChartReady = 'true';

    try {
      const points = readPoints(root);
      if (points.length === 0) {
        setChartState(root, 'empty', '表示できる価格推移データがありません。');
        return;
      }

      const latestDate = getLatestDate(points);
      const listingCountLabel = (root.dataset.listingCountLabel || '').trim();
      root.__tsMarketChartState = {
        allPoints: points,
        latestDate,
        selectedRange: '30d',
        selectedRangeLabel: '直近30日',
        selectedGranularity: 'day',
        activeIndex: 0,
        renderedPoints: [],
        showListingCount: root.dataset.showListingCount === 'true' && listingCountLabel !== '',
        listingCountLabel,
      };

      const updatedDate = resolveUpdatedDate(root, latestDate);
      const updatedNode = root.querySelector('[data-ts-market-chart-updated]');
      if (updatedNode && updatedDate) {
        const updatedKey = dateToKey(updatedDate);
        updatedNode.textContent = updatedKey;
        updatedNode.setAttribute('datetime', updatedKey);
      }

      root.addEventListener('click', (event) => {
        const rangeButton = event.target.closest('[data-ts-market-chart-range]');
        const granularityButton = event.target.closest('[data-ts-market-chart-granularity]');
        if (rangeButton) {
          root.__tsMarketChartState.selectedRange = rangeButton.dataset.tsMarketChartRange;
          root.__tsMarketChartState.selectedRangeLabel = rangeButton.textContent.trim() === '30日'
            ? '直近30日'
            : `直近${rangeButton.textContent.trim()}`;
          hidePoint(root);
          renderChart(root);
        } else if (granularityButton) {
          root.__tsMarketChartState.selectedGranularity =
            granularityButton.dataset.tsMarketChartGranularity;
          hidePoint(root);
          renderChart(root);
        }
      });

      const svg = root.querySelector('[data-ts-market-chart-svg]');
      const showNearestPointer = (event) => {
        const state = root.__tsMarketChartState;
        if (!state.renderedPoints.length || !svg.getScreenCTM()) return;
        const svgPoint = svg.createSVGPoint();
        svgPoint.x = event.clientX;
        svgPoint.y = event.clientY;
        const localPoint = svgPoint.matrixTransform(svg.getScreenCTM().inverse());
        let nearestIndex = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;
        state.renderedPoints.forEach((point, index) => {
          const distance = Math.abs(point.x - localPoint.x);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        });
        showPoint(root, nearestIndex, { x: event.clientX, y: event.clientY });
      };
      svg.addEventListener('pointermove', showNearestPointer);
      svg.addEventListener('pointerdown', (event) => {
        showNearestPointer(event);
      });
      svg.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'touch') hidePoint(root);
      });
      svg.addEventListener('focus', () => showPoint(root, root.__tsMarketChartState.activeIndex));
      svg.addEventListener('blur', () => hidePoint(root));
      svg.addEventListener('keydown', (event) => {
        const state = root.__tsMarketChartState;
        if (!state.renderedPoints.length) return;
        let nextIndex = state.activeIndex;
        if (event.key === 'ArrowLeft') nextIndex -= 1;
        else if (event.key === 'ArrowRight') nextIndex += 1;
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = state.renderedPoints.length - 1;
        else if (event.key === 'Escape') {
          hidePoint(root);
          return;
        } else {
          return;
        }
        event.preventDefault();
        showPoint(root, nextIndex);
      });

      renderChart(root);

      if (typeof ResizeObserver === 'function') {
        const resizeObserver = new ResizeObserver(() => {
          const state = root.__tsMarketChartState;
          if (!state) return;
          const compact = getChartLayout(root, state.showListingCount).compact;
          if (compact !== state.compact) {
            hidePoint(root);
            renderChart(root);
          }
        });
        resizeObserver.observe(root);
        root.__tsMarketChartState.resizeObserver = resizeObserver;
      }
    } catch (error) {
      console.error('[Timeseek market price chart]', error);
      setChartState(root, 'error', '価格推移グラフを表示できませんでした。');
    }
  };

  const boot = (scope = document) => {
    if (!scope) return;
    const roots = [];
    if (scope.matches?.('[data-ts-market-price-chart]')) roots.push(scope);
    scope.querySelectorAll?.('[data-ts-market-price-chart]').forEach((root) => roots.push(root));
    roots.forEach(initialize);
  };

  return {
    DEFAULT_PRICE_OUTLIER_THRESHOLD,
    RANGE_OPTIONS,
    aggregateByMonth,
    boot,
    buildChartSummaryText,
    buildTooltipRows,
    createInventoryScale,
    createPriceScale,
    dateToKey,
    filterByRange,
    formatInventoryAxis,
    formatListingCount,
    formatXAxisLabel,
    formatPriceAxis,
    getLatestDate,
    getRangeStart,
    isPlausiblePrice,
    isPlaceholderPrice,
    normalizePoint,
    normalizePoints,
    parseDateValue,
    rangeAreaPath,
    sanitizePriceRange,
  };
});
